import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, Chip, Stack, Typography, Divider, Button, TablePagination, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';
import dayjs from 'dayjs';
import JobsContainersGroup from './JobsContainersGroup';
import ConfirmJobDialog from './ConfirmJobDialog';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const columns = [
  { key: 'Preparing Vehicles', title: 'Preparing' },
  { key: 'Loading Vehicles', title: 'Loading' },
  { key: 'Finished', title: 'Finished' },
  { key: 'Canceled', title: 'Canceled' },
];

const JobCard = ({ job, onConfirm }) => {
  const lrd = job?.booking?.latestReturnDate;
  const erd = job?.booking?.earliestReceivingDate;
  const diff = lrd ? dayjs(lrd).diff(dayjs(), 'day') : null;
  const vehiclesCount = (job?.containers || []).reduce((a, c) => a + (c?.shipmentItems?.length || 0), 0);
  const hasHybridElectric = (job?.containers || []).some((c) =>
    (c?.shipmentItems || []).some((s) => {
      const ft = s?.vehicle?.fuelType?.toLowerCase();
      return ft === 'hybrid' || ft === 'electric';
    })
  );
  const booking = job?.booking;
  const start = job?.startDate;
  const startDay = start ? dayjs(start) : null;
  const isStartToday = !!(startDay && startDay.isSame(dayjs(), 'day'));
  const isStartLate = !!(startDay && startDay.isBefore(dayjs(), 'day'));

  const pulseWarn = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, .6); }
    70% { box-shadow: 0 0 0 6px rgba(255, 193, 7, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
  `;
  const pulseErr = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, .6); }
    70% { box-shadow: 0 0 0 6px rgba(244, 67, 54, 0); }
    100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
  `;

  return (
    <Card variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
      <CardContent sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">#{job.id}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {hasHybridElectric && (
                    <Tooltip title="Hybrid/Electric vehicles included" arrow>
                      <Chip
                        size="small"
                        label="Hybrid/Electric"
                        color="error"
                        variant="outlined"
                        sx={{
                          animation: `${pulseWarn} 1.6s ease-out infinite`,
                        }}
                        icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
                      />
                    </Tooltip>
                  )}
              {((isStartToday || isStartLate)&& job.jobStatus !== "Finished" && job.jobStatus !== "Canceled") && (
                <>
                  <Tooltip title={isStartLate ? 'Job Late' : 'Job Today'} arrow>
                    <Chip
                      size="small"
                      label={isStartLate ? 'Job Late' : 'Job Today'}
                      color={isStartLate ? 'error' : 'warning'}
                      sx={{
                        animation: `${isStartLate ? pulseErr : pulseWarn} 1.6s ease-out infinite`,
                      }}
                    />
                  </Tooltip>
                  
                </>
              )}
              
              <Chip size="small" label={job.jobStatus} color={job.jobStatus === 'Loading Vehicles' ? 'primary' : job.jobStatus === 'Finished' ? 'success' : job.jobStatus === 'Canceled' ? 'error' : 'warning'} />
            </Stack>
          </Stack>
            <Stack sx={{pt:1}} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
              <Typography variant="caption"><strong>Booking:</strong></Typography>
                <Typography variant="body2">{booking?.number || '-'}</Typography>

            </Box>

          <Typography variant="body2" color="text.secondary">
            {job?.client?.name +" (" + job?.client?.company + ") " || '-'}
          </Typography>
          </Stack>


            <Stack sx={{pt:1}} direction="row" spacing={1} alignItems="center" justifyContent="space-between">

            <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Start Job:</strong></Typography>
                <Typography variant="body2">{job.startDate || '-'}</Typography>
              </Box>

          </Stack>






          <Divider sx={{ my: 0.5, pt: 2 }} />

          {/* Shipment snapshot */}
          <Box sx={{ color: 'text.secondary', pt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>

              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Booking:</strong></Typography>
                <Typography variant="body2">{booking?.number || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Vessel:</strong></Typography>
                <Typography variant="body2">{booking?.vesselName || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>POL:</strong></Typography>
                <Typography variant="body2">{booking?.portLoading?.name || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Dest:</strong></Typography>
                <Typography variant="body2">{booking?.destination?.name || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Line:</strong></Typography>
                <Typography variant="body2">{booking?.shippingLine?.name || '-'}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 0.5, pt: 2 }} />

          {/* Dates & stats */}
          <Box sx={{ color: 'text.secondary', pt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1, alignItems: 'center'}}>
                <Typography variant="caption"><strong>Start:</strong></Typography>
                <Typography variant="body2">{job.startDate || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>ERD:</strong></Typography>
                <Typography variant="body2">{erd || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>LRD:</strong></Typography>
                <Typography variant="body2">{lrd || '-'}</Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Days:</strong></Typography>
                <Typography variant="body2" sx={{ color: typeof diff === 'number' ? (diff < 0 ? 'error.main' : diff <= 2 ? 'warning.main' : 'text.primary') : 'text.primary' }}>
                  {typeof diff === 'number' ? `${diff}d` : '-'}
                </Typography>
              </Box>
              <Box sx={{display:'flex', flexDirection:'row' , gap: 1}}>
                <Typography variant="caption"><strong>Vehicles:</strong></Typography>
                <Typography variant="body2">{vehiclesCount}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 0.5, pt: 2 }} />

          {/* Containers grouped: container info => vehicles */}
          <Box sx={{ pt: 1 }}>
            <JobsContainersGroup job={job} />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={job.jobStatus === 'Finished' || job.jobStatus === 'Canceled'}
              onClick={() => onConfirm && onConfirm(job)}
            >
              Confirm Completed
            </Button>
          </Box>
      </CardContent>
    </Card>
  );
};

const JobsBoard = ({ onConfirmDialogOpenChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmState, setConfirmState] = useState({ open: false, job: null });
  const stateV2 = useSelector((s) => s.manageV2.getManage);
  const legacy = useSelector((s) => s.manage);
  const raw = (stateV2?.object && Object.keys(stateV2.object || {}).length > 0) ? stateV2.object : (legacy?.object || {});
  const page = raw?.body && (Array.isArray(raw.body.content) || typeof raw.body.totalElements === 'number') ? raw.body : raw;
  const grouped = useMemo(() => {
    const items = page.content || [];
    const map = { 'Preparing Vehicles': [], 'Loading Vehicles': [], Finished: [], Canceled: [] };
    items.forEach((j) => {
      const k = map[j.jobStatus] ? j.jobStatus : 'Preparing Vehicles';
      map[k].push(j);
    });
    return map;
  }, [page.content]);

  // Derive current status from URL query; fallback to Preparing Vehicles
  const statusFromUrl = searchParams.get('Status') || 'Preparing Vehicles';
  const activeKey = columns.some((c) => c.key === statusFromUrl) ? statusFromUrl : 'Preparing Vehicles';
  const activeCol = columns.find((c) => c.key === activeKey) || columns[0];
  // Pagination data from backend or URL params as fallback
  const totalElements = Number.isFinite(page?.totalElements) ? page.totalElements : 0;
  const pageNumber = Number.isFinite(page?.number) ? page.number : parseInt(searchParams.get('PageNumber') || '0', 10);
  const pageSize = Number.isFinite(page?.size) ? page.size : parseInt(searchParams.get('PageSize') || '10', 10);

  const handleChangePage = (_e, newPage) => {
    const next = new URLSearchParams(searchParams);
    next.set('PageNumber', String(newPage));
    setSearchParams(next);
    // scroll to top of the list/container for better UX
    try {
      const el = document.querySelector('#root') || window;
      if ('scrollTo' in el) {
        // @ts-ignore
        el.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (_) { /* noop */ }
  };

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    const next = new URLSearchParams(searchParams);
    next.set('PageSize', String(newSize));
    next.set('PageNumber', '0');
    setSearchParams(next);
  };

  return (
    <Box>
      <ConfirmJobDialog
        open={confirmState.open}
        job={confirmState.job}
        onClose={() => { setConfirmState({ open: false, job: null }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(false); }}
        searchParams={searchParams}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e9ecef', borderRadius: 2, p: 1.5, minHeight: 240 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0B3C5D', fontWeight: 700 }}>{activeCol.title}</Typography>
            <Chip size="small" label={grouped[activeKey]?.length || 0} />
          </Stack>
          {grouped[activeKey] && grouped[activeKey].length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              {grouped[activeKey].map((job) => (
                <JobCard key={job.id} job={job} onConfirm={(j) => { setConfirmState({ open: true, job: j }); onConfirmDialogOpenChange && onConfirmDialogOpenChange(true); }} />
              ))}
            </Box>
          )}
          {(!grouped[activeKey] || grouped[activeKey].length === 0) && (
            <Typography variant="caption" color="text.secondary">No jobs</Typography>
          )}
          <Divider sx={{ mt: 1 }} />
          {/* Pagination (sticky) */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mb: 2,  bottom: 0, bgcolor: '#f8fafc', borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={totalElements || 0}
              page={Math.max(0, pageNumber || 0)}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize || 10}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default JobsBoard;
